import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "./RestService";
import IOnGetSerialsDTO from "../models/DTO/IOnGetSerialsDTO";
import IGetSerialsQuery from "@models/DTO/IGetSerialsQuery";
import IBulkAssignSerialsDTO from "../models/DTO/IBulkAssignSerialsDTO";
import IBulkCreateSerialsDTO from "../models/DTO/IBulkCreateSerialsDTO";
import IProcessSerialDTO from "../models/DTO/IProcessSerialDTO";
import IOnBulkAssignSerialsDTO from "../models/DTO/IOnBulkAssignSerialsDTO";
import IReclassifySerialDTO from "../models/DTO/IReclassifySerialDTO";
import IOnReclassifySerialDTO from "../models/DTO/IOnReclassifySerialDTO";

const getSerials = async (
    query: IGetSerialsQuery,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnGetSerialsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsGetAll,
        query,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
    });

    if (!response.ok) {
        throw new Error(`Failed to fetch serials: ${response.statusText}`);
    }

    return (await response.json()) as IOnGetSerialsDTO;
};

/**
 * Assign serials to one order line. Resolves with the per-serial outcome: a serial that fails
 * (wrong SKU, already used, over the line's quantity) is listed in `errors`, it does not reject.
 */
const bulkAssignSerials = async (
    dto: IBulkAssignSerialsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnBulkAssignSerialsDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsBulkAssign,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(dto),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to bulk assign serials: ${response.statusText}`);
    }

    // The API always answers { assigned, errors }. Anything else is a broken response, not "nothing
    // assigned": reject it rather than report a silent no-op.
    const result: unknown = await response.json().catch(() => null);
    if (!isBulkAssignOutcome(result)) {
        throw new Error("Invalid bulk assign response");
    }

    return result;
};

const isBulkAssignOutcome = (value: unknown): value is IOnBulkAssignSerialsDTO => {
    const outcome = value as IOnBulkAssignSerialsDTO | null;
    return (
        typeof outcome === "object" &&
        outcome !== null &&
        typeof outcome.assigned === "number" &&
        Array.isArray(outcome.errors) &&
        outcome.errors.every((error) => typeof error?.serialId === "number" && typeof error?.reason === "string")
    );
};

/**
 * Set a serial's finished product (re-SKU, OR-1459). The serial number never changes; only its
 * catalog item does. The API moves the serial's inventory contribution and BOM consumption across.
 */
const reclassifySerial = async (
    serialId: number,
    dto: IReclassifySerialDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnReclassifySerialDTO> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsReclassify,
        params: { serialId: String(serialId) },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(dto),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to reclassify serial: ${response.statusText}`);
    }

    return (await response.json()) as IOnReclassifySerialDTO;
};

const deleteSerial = async (
    serialId: number,
    serialValue: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<void> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsProcessSerial,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const body: Partial<IProcessSerialDTO> = {
        serial: serialValue,
        serialId,
        deleteFlag: true,
    };

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to delete serial: ${response.statusText}`);
    }
};

const bulkCreateSerials = async (
    dto: IBulkCreateSerialsDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<void> => {
    const url = buildRestUrlWithQuery({
        endpoint: RestEndpointsEnum.SerialsBulkCreate,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(dto),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || `Failed to bulk create serials: ${response.statusText}`);
    }
};

const SerialService = {
    getSerials,
    bulkAssignSerials,
    bulkCreateSerials,
    deleteSerial,
    reclassifySerial,
};

export default SerialService;
